import { useTranslation } from '../../i18n/useTranslation';
import React, { useState } from 'react';
import {
  DollarSign,
  TrendingUp,
  PieChart,
  Building2,
  Calculator,
  ShieldCheck,
  Scale,
  Briefcase,
  Calendar,
  Layers,
  Plus,
  Zap,
  Award,
  FileText,
  CheckCircle2,
  AlertTriangle,
  Compass,
  Lock,
  Unlock,
  RefreshCw,
  SlidersHorizontal,
  ArrowUpRight,
  HelpCircle
} from 'lucide-react';
import { AppView } from '../../types';
import {
  corporateFinanceStore,
  RevenueStream,
  FinanceMetric,
  ForecastScenario,
  UnitEconomicsModel,
  TreasuryAccount,
  CashForecastWeek,
  FinancialControl,
  BoardFinancePack
} from '../../services/finance/corporateFinanceStore';
import { CorporateFinanceScenarioStudio } from './CorporateFinanceScenarioStudio';

interface ChiefFinancialOfficerCommandCenterProps {
  onNavigateToView: (view: AppView) => void;
}

export const ChiefFinancialOfficerCommandCenter: React.FC<ChiefFinancialOfficerCommandCenterProps> = ({
  onNavigateToView
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<
    | 'overview'
    | 'revenue-streams'
    | 'fpa-budgeting'
    | 'scenarios'
    | 'unit-economics'
    | 'treasury-cash'
    | 'controls-governance'
    | 'board-pack'
  >('overview');

  const [isScenarioStudioOpen, setIsScenarioStudioOpen] = useState<boolean>(false);

  const categories = corporateFinanceStore.getCategories();
  const costCenters = corporateFinanceStore.getCostCenters();
  const revenueStreams = corporateFinanceStore.getRevenueStreams();
  const metrics = corporateFinanceStore.getMetrics();
  const scenarios = corporateFinanceStore.getScenarios();
  const unitEconomics = corporateFinanceStore.getUnitEconomics();
  const treasuryAccounts = corporateFinanceStore.getTreasuryAccounts();
  const cashForecast13W = corporateFinanceStore.getCashForecast13W();
  const controls = corporateFinanceStore.getControls();
  const boardPack = corporateFinanceStore.getBoardPack();

  const actualMrrMetric = metrics.find((m) => m.key === 'ACTUAL_MRR');
  const grossMarginMetric = metrics.find((m) => m.key === 'GROSS_MARGIN_PCT');
  const netBurnMetric = metrics.find((m) => m.key === 'MONTHLY_NET_BURN');
  const runwayMetric = metrics.find((m) => m.key === 'CASH_RUNWAY_MONTHS');

  const totalCashBalanceEur = treasuryAccounts.reduce((acc, curr) => acc + curr.balanceAmount, 0);

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#1E293B] font-sans pb-16">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-20 shadow-2xs">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-emerald-600 text-white flex items-center justify-center shadow-sm">
              <DollarSign className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold tracking-wider text-emerald-800 uppercase bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-200">
                  Corporate Finance Operating System
                </span>
                <span className="text-xs text-[#64748B] font-mono">P30 Enterprise</span>
              </div>
              <h1 className="text-xl font-black text-[#0F172A] tracking-tight">
                Chief Financial Officer (CFO) Command Center
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Master Switch Status Banner */}
            <div className="px-3 py-1.5 bg-slate-100 rounded-lg border border-slate-300 text-xs font-bold text-slate-700 flex items-center gap-2">
              <Lock className="w-3.5 h-3.5 text-amber-600" />
              <span>Paid Monetization Switch: <strong className="text-amber-800">DISABLED (Scenario Mode)</strong></span>
            </div>

            <button
              onClick={() => setIsScenarioStudioOpen(true)}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-bold flex items-center gap-2 transition-colors cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              Nouveau Scénario FP&A
            </button>
            <button
              onClick={() => onNavigateToView('cpo-command-center')}
              className="px-4 py-2 bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 rounded-lg text-sm font-semibold flex items-center gap-2 transition-colors cursor-pointer"
            >
              <Compass className="w-4 h-4 text-blue-600" />
              CPO Operating System (P29)
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="max-w-7xl mx-auto px-6 overflow-x-auto flex gap-1 border-t border-[#F1F5F9]">
          {[
            { id: 'overview', label: 'Vue Exécutive CFO', icon: PieChart },
            { id: 'revenue-streams', label: 'Revenus & Traçabilité Encaissés', icon: TrendingUp },
            { id: 'fpa-budgeting', label: t('cfo.tab.budget'), icon: Briefcase },
            { id: 'scenarios', label: 'Scénarios & Test de Résistance', icon: Calculator },
            { id: 'unit-economics', label: 'Unit Economics (CAC, LTV, Payback)', icon: Scale },
            { id: 'treasury-cash', label: 'Trésorerie & Prévisions 13 Semaines', icon: Building2 },
            { id: 'controls-governance', label: 'Contrôles Financiers & Séparation', icon: ShieldCheck },
            { id: 'board-pack', label: 'Board Pack Certifié (Q3 2026)', icon: Award }
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`px-4 py-3 text-sm font-bold flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                  isActive
                    ? 'border-emerald-600 text-emerald-800 bg-emerald-50/50'
                    : 'border-transparent text-[#64748B] hover:text-[#0F172A] hover:bg-slate-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </header>

      {/* Main Container */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* EXECUTIVE OVERVIEW */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              <div className="bg-white p-5 rounded-xl border border-[#E2E8F0] shadow-2xs">
                <div className="text-xs font-semibold text-[#64748B] uppercase">Revenu Mensuel Réel</div>
                <div className="text-2xl font-black text-[#0F172A] mt-1">{actualMrrMetric?.value.toLocaleString()} €</div>
                <div className="text-xs text-emerald-700 font-semibold mt-1">Confirmed Provenance</div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-[#E2E8F0] shadow-2xs">
                <div className="text-xs font-semibold text-[#64748B] uppercase">Marge Brute (Gross)</div>
                <div className="text-2xl font-black text-emerald-700 mt-1">{grossMarginMetric?.value}%</div>
                <div className="text-xs text-emerald-800 font-semibold mt-1">Post FinOps & AI Tokens</div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-[#E2E8F0] shadow-2xs">
                <div className="text-xs font-semibold text-[#64748B] uppercase">Net Burn Mensuel</div>
                <div className="text-2xl font-black text-slate-800 mt-1">{netBurnMetric?.value.toLocaleString()} €</div>
                <div className="text-xs text-slate-600 font-semibold mt-1">Après Dépenses Exploitation</div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-[#E2E8F0] shadow-2xs">
                <div className="text-xs font-semibold text-[#64748B] uppercase">Trésorerie Disponible</div>
                <div className="text-2xl font-black text-blue-700 mt-1">{(totalCashBalanceEur / 1000).toFixed(0)}k €</div>
                <div className="text-xs text-blue-800 font-semibold mt-1">3 Comptes Multi-Devises</div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-[#E2E8F0] shadow-2xs">
                <div className="text-xs font-semibold text-[#64748B] uppercase">Runway (Base Case)</div>
                <div className="text-2xl font-black text-indigo-700 mt-1">{runwayMetric?.value} Mois</div>
                <div className="text-xs text-indigo-800 font-semibold mt-1">Autonomie Sécurisée</div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-[#E2E8F0] shadow-2xs">
                <div className="text-xs font-semibold text-[#64748B] uppercase">Contrôles Financiers</div>
                <div className="text-2xl font-black text-teal-700 mt-1">100%</div>
                <div className="text-xs text-teal-800 font-semibold mt-1">3/3 Contrôles Valides</div>
              </div>
            </div>

            {/* Directives & Core Rules */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-[#0F172A] flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-emerald-600" />
                    Principes Inviolables de la Finance Corporate YamooPay
                  </h2>
                  <span className="px-2.5 py-1 bg-emerald-50 text-emerald-800 border border-emerald-200 text-xs font-bold rounded-full">
                    Financial Rigor
                  </span>
                </div>

                <div className="space-y-3 text-xs text-[#475569]">
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <strong className="text-slate-900 block font-bold">1. BILLING ≠ REVENUE & CASH ≠ REVENUE</strong>
                    Aucun revenu n&apos;est comptabilisé sur la simple émission d&apos;une facture ou la réception d&apos;une estimation indicative. Seules les données prouvées (Rapports certifiés AdSense/Affiliation ou encaissements confirmés) sont admises.
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <strong className="text-slate-900 block font-bold">2. MASTER SWITCH MONETIZATION (PROMPT 21)</strong>
                    Tant que le Master Switch est désactivé, le chiffre d&apos;affaires réel des abonnements/API payants reste à ZÉRO. Les prévisions d&apos;abonnements sont strictement cantonnées au module Scénarios.
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <strong className="text-slate-900 block font-bold">3. NO GHOST EXPENSES & ZERO AUDIT LACK</strong>
                    Chaque chiffre présenté au Board ou à la Direction Générale est traçable jusqu&apos;à son centre de coût, sa devise d&apos;origine et sa règle d&apos;allocation transparente.
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-[#0F172A] flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-indigo-600" />
                    Répartition de la Trésorerie par Établissement
                  </h2>
                </div>

                <div className="space-y-3">
                  {treasuryAccounts.map((acc) => (
                    <div key={acc.id} className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-between text-xs">
                      <div>
                        <div className="font-bold text-[#0F172A] text-sm">{acc.bankName}</div>
                        <div className="text-slate-500 mt-0.5 font-mono">
                          {acc.legalEntity} • {acc.maskedIdentifier}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-black text-slate-900 font-mono">
                          {acc.balanceAmount.toLocaleString()} {acc.currency}
                        </div>
                        <span className="text-[10px] text-emerald-700 font-bold bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200 inline-block mt-0.5">
                          Reconciled
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* REVENUE STREAMS */}
        {activeTab === 'revenue-streams' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
              <h2 className="text-lg font-bold text-slate-900 mb-1">Registre des Sources de Revenus & Provenances Certifiées</h2>
              <p className="text-sm text-slate-600 mb-6">Distinction stricte entre revenus réels encaissés et projections de scénarios.</p>

              <div className="space-y-4">
                {revenueStreams.map((stream) => (
                  <div key={stream.id} className="p-4 border border-slate-200 rounded-xl bg-slate-50/50 space-y-2 text-xs">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-slate-900 text-sm">{stream.name}</span>
                        <span className="px-2.5 py-0.5 bg-blue-50 text-blue-700 rounded-full font-bold text-[10px] border border-blue-200">
                          {stream.type}
                        </span>
                      </div>
                      <span className="font-mono text-emerald-700 font-bold text-sm">
                        {stream.mrrContributionMonthly.toLocaleString()} € / mois
                      </span>
                    </div>

                    <p className="text-slate-600 font-mono">{stream.notes}</p>

                    <div className="pt-2 border-t border-slate-200 flex justify-between items-center text-slate-500 font-mono">
                      <span>Provenances: <strong>{stream.provenance}</strong></span>
                      <span>Marge Brute Déduite: <strong className="text-emerald-700">{stream.grossMarginPct}%</strong></span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* FP&A & BUDGETING */}
        {activeTab === 'fpa-budgeting' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
                <h2 className="text-lg font-bold text-slate-900 mb-1">{t('cfo.budget.title')}</h2>
                <p className="text-sm text-slate-600 mb-4">Allocation explicite des dépenses d&apos;exploitation par département.</p>

                <div className="space-y-3">
                  {costCenters.map((cc) => (
                    <div key={cc.code} className="p-3.5 border border-slate-200 rounded-xl bg-slate-50/50 space-y-1 text-xs">
                      <div className="flex justify-between font-bold text-slate-900">
                        <span>{cc.name}</span>
                        <span className="font-mono text-slate-500">{cc.code}</span>
                      </div>
                      <div className="text-slate-600">Département: {cc.department} • Owner: {cc.owner}</div>
                      <div className="text-slate-500 font-mono text-[10px]">{cc.legalEntity}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
                <h2 className="text-lg font-bold text-slate-900 mb-1">Nomenclature Comptable & FP&A</h2>
                <p className="text-sm text-slate-600 mb-4">Plan comptable analytique unifié de YamooPay.</p>

                <div className="space-y-3">
                  {categories.map((cat) => (
                    <div key={cat.code} className="p-3 border border-slate-200 rounded-xl bg-slate-50/50 space-y-1 text-xs">
                      <div className="flex justify-between font-bold">
                        <span className="text-slate-900">{cat.name}</span>
                        <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded font-mono text-[10px] border border-purple-200">
                          {cat.type}
                        </span>
                      </div>
                      <p className="text-slate-600">{cat.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SCENARIOS & STRESS TESTS */}
        {activeTab === 'scenarios' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Scénarios FP&A & Tests de Résistance</h2>
                  <p className="text-sm text-slate-600">Modélisation de sensibilité sans altération des comptes réels.</p>
                </div>
                <button
                  onClick={() => setIsScenarioStudioOpen(true)}
                  className="px-3.5 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 cursor-pointer"
                >
                  <Plus className="w-3.5 h-3.5" /> Créer Scénario
                </button>
              </div>

              <div className="space-y-4">
                {scenarios.map((scen) => (
                  <div key={scen.id} className="p-5 border border-slate-200 rounded-xl bg-slate-50/40 space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="px-2.5 py-0.5 bg-indigo-50 text-indigo-700 font-bold text-xs rounded-full border border-indigo-200">
                          {scen.type} SCENARIO
                        </span>
                        <h3 className="text-base font-bold text-slate-900 mt-1">{scen.name}</h3>
                      </div>
                      <span className="text-xs font-mono text-slate-500 bg-white px-3 py-1 rounded border border-slate-200">
                        Paid Switch: {scen.isMonetizationMasterSwitchOn ? 'SIMULATED ON' : 'OFF'}
                      </span>
                    </div>

                    <p className="text-xs text-slate-700 font-mono bg-white p-3 rounded-lg border border-slate-200">
                      Notes: {scen.notes}
                    </p>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                      <div className="p-2.5 bg-emerald-50/50 rounded-lg border border-emerald-200">
                        <strong className="text-emerald-900 block font-bold">Revenu Annuel Projeté:</strong>
                        <span className="text-emerald-800 font-mono">{scen.projectedAnnualRevenueEur.toLocaleString()} €</span>
                      </div>
                      <div className="p-2.5 bg-blue-50/50 rounded-lg border border-blue-200">
                        <strong className="text-blue-900 block font-bold">Marge Brute Estimée:</strong>
                        <span className="text-blue-800 font-mono">{scen.projectedGrossMarginPct}%</span>
                      </div>
                      <div className="p-2.5 bg-slate-100 rounded-lg border border-slate-200">
                        <strong className="text-slate-900 block font-bold">Net Burn Mensuel:</strong>
                        <span className="text-slate-800 font-mono">{scen.projectedMonthlyBurnEur.toLocaleString()} €</span>
                      </div>
                      <div className="p-2.5 bg-purple-50/50 rounded-lg border border-purple-200">
                        <strong className="text-purple-900 block font-bold">Runway Projeté:</strong>
                        <span className="text-purple-800 font-mono font-bold">{scen.projectedRunwayMonths} Mois</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* UNIT ECONOMICS */}
        {activeTab === 'unit-economics' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
              <h2 className="text-lg font-bold text-slate-900 mb-1">Modèles de Unit Economics par Segment Client</h2>
              <p className="text-sm text-slate-600 mb-6">Gouvernance métrique rigoureuse des CAC, LTV, Payback et coûts par calcul déterministe.</p>

              <div className="space-y-4">
                {unitEconomics.map((ue) => (
                  <div key={ue.id} className="p-5 border border-slate-200 rounded-xl bg-slate-50/50 space-y-3">
                    <h3 className="text-base font-bold text-slate-900">{ue.segmentName}</h3>
                    <p className="text-xs text-slate-600">{ue.notes}</p>

                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 text-xs">
                      <div className="p-2.5 bg-white rounded border border-slate-200">
                        <span className="text-slate-500 block">Blended CAC:</span>
                        <strong className="text-slate-900 font-mono">{ue.blendedCacEur} €</strong>
                      </div>
                      <div className="p-2.5 bg-white rounded border border-slate-200">
                        <span className="text-slate-500 block">LTV 36-Mois:</span>
                        <strong className="text-emerald-700 font-mono">{ue.ltv36MonthsEur} €</strong>
                      </div>
                      <div className="p-2.5 bg-white rounded border border-slate-200">
                        <span className="text-slate-500 block">Ratio LTV / CAC:</span>
                        <strong className="text-indigo-700 font-mono">{ue.ltvToCacRatio}x</strong>
                      </div>
                      <div className="p-2.5 bg-white rounded border border-slate-200">
                        <span className="text-slate-500 block">Payback CAC:</span>
                        <strong className="text-purple-700 font-mono">{ue.cacPaybackMonths} Mois</strong>
                      </div>
                      <div className="p-2.5 bg-white rounded border border-slate-200">
                        <span className="text-slate-500 block">ARPU Mensuel:</span>
                        <strong className="text-slate-900 font-mono">{ue.arpuMonthlyEur} €</strong>
                      </div>
                      <div className="p-2.5 bg-white rounded border border-slate-200">
                        <span className="text-slate-500 block">Coût / Calcul:</span>
                        <strong className="text-teal-700 font-mono">{ue.costPerCalculationEur} €</strong>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* TREASURY & CASH FORECAST */}
        {activeTab === 'treasury-cash' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
              <h2 className="text-lg font-bold text-slate-900 mb-1">Prévisions de Trésorerie à 13 Semaines (Cash Waterfall)</h2>
              <p className="text-sm text-slate-600 mb-6">Suivi hebdomadaire des encaissements et décaissements de trésorerie.</p>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 text-slate-600 uppercase border-b border-slate-200 font-bold">
                    <tr>
                      <th className="p-3">Semaine</th>
                      <th className="p-3">Cash Début</th>
                      <th className="p-3 text-emerald-700">Encaissements (+</th>
                      <th className="p-3 text-rose-700">Masse Salariale (-</th>
                      <th className="p-3 text-rose-700">Fournisseurs (-</th>
                      <th className="p-3 text-rose-700">FinOps Cloud (-</th>
                      <th className="p-3">Cash Fin</th>
                      <th className="p-3">Statut Alerte</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-mono text-slate-700">
                    {cashForecast13W.map((w) => (
                      <tr key={w.weekNumber} className="hover:bg-slate-50">
                        <td className="p-3 font-bold text-slate-900">{w.weekLabel}</td>
                        <td className="p-3">{w.startingCashEur.toLocaleString()} €</td>
                        <td className="p-3 text-emerald-700 font-bold">+{w.projectedCollectionsEur.toLocaleString()} €</td>
                        <td className="p-3 text-rose-700">-{w.projectedPayrollsEur.toLocaleString()} €</td>
                        <td className="p-3 text-rose-700">-{w.projectedVendorOutflowsEur.toLocaleString()} €</td>
                        <td className="p-3 text-rose-700">-{w.projectedCloudFinOpsEur.toLocaleString()} €</td>
                        <td className="p-3 font-bold text-slate-900">{w.endingCashEur.toLocaleString()} €</td>
                        <td className="p-3">
                          <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded font-bold border border-emerald-200">
                            {w.warningLevel}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* CONTROLS & GOVERNANCE */}
        {activeTab === 'controls-governance' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
              <h2 className="text-lg font-bold text-slate-900 mb-1">Cadre de Contrôle Financier & Séparation des Tâches</h2>
              <p className="text-sm text-slate-600 mb-6">Garantie d&apos;intégrité des états financiers et prévention du risque d&apos;erreur.</p>

              <div className="space-y-3">
                {controls.map((ctrl) => (
                  <div key={ctrl.id} className="p-4 border border-slate-200 rounded-xl bg-slate-50/50 space-y-2 text-xs">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold bg-slate-200 text-slate-800 px-2 py-0.5 rounded">{ctrl.code}</span>
                        <span className="font-bold text-slate-900 text-sm">{ctrl.objective}</span>
                      </div>
                      <span className="px-2.5 py-0.5 bg-emerald-50 text-emerald-700 font-bold rounded-full border border-emerald-200">
                        {ctrl.status}
                      </span>
                    </div>

                    <p className="text-slate-600">Risque pallié: <em>{ctrl.riskMitigated}</em></p>

                    <div className="text-slate-500 font-mono pt-2 border-t border-slate-200 flex justify-between">
                      <span>Owner: <strong>{ctrl.owner}</strong> • Type: {ctrl.type}</span>
                      <span>Dernière Exécution: {ctrl.lastExecutedDate}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* BOARD PACK */}
        {activeTab === 'board-pack' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="px-2.5 py-0.5 bg-emerald-50 text-emerald-800 font-bold text-xs rounded-full border border-emerald-200">
                      Status: {boardPack.status}
                    </span>
                    <span className="text-xs text-slate-500 font-mono">Gelé par {boardPack.frozenBy} le {boardPack.frozenAt}</span>
                  </div>
                  <h2 className="text-xl font-black text-[#0F172A] mt-2">Dossier Financier du Conseil d&apos;Administration — {boardPack.period}</h2>
                </div>
              </div>

              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 mb-6">
                <h3 className="text-xs font-bold uppercase text-slate-500 mb-2">Synthèse Exécutive CFO</h3>
                <p className="text-sm text-slate-800 leading-relaxed font-medium">{boardPack.executiveSummary}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs">
                <div className="p-4 bg-emerald-50/40 rounded-xl border border-emerald-200 space-y-2">
                  <h4 className="font-bold text-emerald-900 uppercase">Chiffres Clés Certifiés Board</h4>
                  <ul className="space-y-1 font-mono text-slate-800">
                    <li>Revenu Réel Cumulé: <strong>{boardPack.actualRevenueEur.toLocaleString()} €</strong></li>
                    <li>Marge Brute Globale: <strong>{boardPack.actualGrossMarginPct}%</strong></li>
                    <li>Trésorerie Disponible: <strong>{boardPack.actualCashBalanceEur.toLocaleString()} €</strong></li>
                    <li>Autonomie Financement (Runway): <strong>{boardPack.baseRunwayMonths} Mois</strong></li>
                  </ul>
                </div>

                <div className="p-4 bg-amber-50/40 rounded-xl border border-amber-200 space-y-2">
                  <h4 className="font-bold text-amber-900 uppercase">Matrice des Risques Majeurs & Mitigations</h4>
                  <ul className="list-disc pl-4 space-y-1 text-slate-800">
                    {boardPack.keyRisksAndMitigations.map((risk, i) => (
                      <li key={i}>{risk}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Scenario Studio Modal */}
      {isScenarioStudioOpen && (
        <CorporateFinanceScenarioStudio
          onClose={() => setIsScenarioStudioOpen(false)}
        />
      )}
    </div>
  );
};
